// Central SEO metadata for statically-known routes.
//
// Two consumers:
//
// 1. Per-request SSR. Each page component spreads getSeoRoute('<its path>') into its
//    <SeoHelmet>, and src/entry-server.js / lambda/ssr-handler.js render that <head> into the
//    raw HTML. This is what makes crawlers and social scrapers that DON'T execute JS see the
//    correct per-page tags — client-side react-helmet-async only fills the <head> after
//    hydration, which those crawlers never wait for.
//
// 2. The STATIC SITEMAP. The `seoRoutesJson` plugin in vite.config.js writes this array out as
//    build/seo-routes.json on every client build, which deploys to the site root
//    (https://www.sportskinetic.ai/seo-routes.json). The Laravel sitemap cron reads that file
//    and generates sitemap-static.xml from it. So ADDING A ROUTE HERE IS ALL IT TAKES to get a
//    new landing/guide page into the sitemap — no backend code change, no manual URL list.
//    Conversely, a public page that is NOT in this array will never reach Google via the
//    sitemap. `priority` / `changefreq` below are the sitemap hints for that route; set
//    `sitemap: false` on an entry that must keep its SSR'd <head> but stay out of the sitemap
//    (e.g. a noindex page, or a landing page still awaiting sign-off).
//
// `image`/`imageWidth`/`imageHeight` (og:image) and `twitterImage`/`twitterImageWidth`/
// `twitterImageHeight` (twitter:image) are set on every route to the DEFAULT_* constants below
// unless a route needs its own distinct image, in which case override them on that route's entry.
//
// Only STATIC routes belong here. Dynamic routes (/news-article/:id, /playerdetail/:slug,
// /teams-listing/:id, /reports/team-transfer-analysis/:id) have no fixed URL list, so their
// <head> is built per-request from API data by lambda/ssr-handler.js, and their sitemap
// entries are generated by Laravel straight from the database — not from this file.
//
// Copy is the approved SEO metadata map (British spelling, keyword-led titles, unique
// descriptions). KEEP IN SYNC with the <SeoHelmet .../> props in each page component, or the
// SSR'd <head> and the client-rendered DOM will disagree.

// Explicit .js extension: vite.config.js imports this file in plain Node at build time.
import { MONTHLY_PRICE, YEARLY_PRICE, formatPrice } from '../helpers/pricingPlans.js';

export const SITE_URL = 'https://www.sportskinetic.ai';

// Site-wide default og:image / twitter:image for statically-known routes. Every route below
// sets these explicitly (rather than omitting `image`/`twitterImage` and relying on plugin
// fallback logic) so each entry stays self-describing. Update the WIDTH/HEIGHT constants if
// the source files' pixel dimensions ever change.
const DEFAULT_OG_IMAGE = '/assets/facebook-og-image0926.png';
const DEFAULT_OG_IMAGE_WIDTH = 1732;
const DEFAULT_OG_IMAGE_HEIGHT = 908;
const DEFAULT_TWITTER_IMAGE = '/assets/twitter-og-image0926.png';
const DEFAULT_TWITTER_IMAGE_WIDTH = 1605;
const DEFAULT_TWITTER_IMAGE_HEIGHT = 980;

export const seoRoutes = [
    {
        // The homepage is rendered by LoggedOutHomePlusTwo (see src/ssr/routes.js), which pulls
        // these values via getSeoRoute('/') — so '/' belongs here like any other static route,
        // and the sitemap picks it up automatically instead of the backend hardcoding it.
        path: '/',
        priority: 1.0,
        changefreq: 'daily',
        title: 'AI Football Predictions, News & Insights | SportsKinetic',
        description: 'SportsKinetic delivers AI-powered football predictions, match previews, player insights, transfer news and fixture analysis, all in one smart platform.',
        image: DEFAULT_OG_IMAGE,
        imageWidth: DEFAULT_OG_IMAGE_WIDTH,
        imageHeight: DEFAULT_OG_IMAGE_HEIGHT,
        twitterImage: DEFAULT_TWITTER_IMAGE,
        twitterImageWidth: DEFAULT_TWITTER_IMAGE_WIDTH,
        twitterImageHeight: DEFAULT_TWITTER_IMAGE_HEIGHT,
    },
    {
        path: '/how-it-works',
        priority: 0.8,
        changefreq: 'monthly',
        title: 'How Our AI Football Prediction Engine Works | SportsKinetic',
        description: "See how SportsKinetic's AI analyses fixtures, form and player data to generate accurate football predictions and match insights. Here's how it works.",
        image: DEFAULT_OG_IMAGE,
        imageWidth: DEFAULT_OG_IMAGE_WIDTH,
        imageHeight: DEFAULT_OG_IMAGE_HEIGHT,
        twitterImage: DEFAULT_TWITTER_IMAGE,
        twitterImageWidth: DEFAULT_TWITTER_IMAGE_WIDTH,
        twitterImageHeight: DEFAULT_TWITTER_IMAGE_HEIGHT,
    },
    {
        path: '/about-us',
        priority: 0.6,
        changefreq: 'monthly',
        title: 'About SportsKinetic | Defence-Grade Football Intelligence',
        description: 'SportsKinetic is not a prediction site, it is an intelligence system. Discover the Ianic Sports Insight Engine, our AI agents, our principles and the team behind it.',
        image: DEFAULT_OG_IMAGE,
        imageWidth: DEFAULT_OG_IMAGE_WIDTH,
        imageHeight: DEFAULT_OG_IMAGE_HEIGHT,
        twitterImage: DEFAULT_TWITTER_IMAGE,
        twitterImageWidth: DEFAULT_TWITTER_IMAGE_WIDTH,
        twitterImageHeight: DEFAULT_TWITTER_IMAGE_HEIGHT,
    },
    {
        path: '/pricing',
        priority: 0.8,
        changefreq: 'monthly',
        title: `SportsKinetic Pricing | Plans from ${formatPrice(YEARLY_PRICE)} a Year`,
        description: `Unlock AI football predictions, exact scores, goal scorers and match intelligence. ${formatPrice(MONTHLY_PRICE)} a month or ${formatPrice(YEARLY_PRICE)} a year, with a 7-day free trial. Cancel anytime.`,
        image: DEFAULT_OG_IMAGE,
        imageWidth: DEFAULT_OG_IMAGE_WIDTH,
        imageHeight: DEFAULT_OG_IMAGE_HEIGHT,
        twitterImage: DEFAULT_TWITTER_IMAGE,
        twitterImageWidth: DEFAULT_TWITTER_IMAGE_WIDTH,
        twitterImageHeight: DEFAULT_TWITTER_IMAGE_HEIGHT,
    },
    {
        path: '/premier-league',
        priority: 0.9,
        changefreq: 'weekly',
        title: 'Premier League AI Predictions 2026-27 | SportsKinetic',
        description: 'Get AI-powered Premier League 2026-27 predictions, exact score forecasts, match insights, and data-driven analysis for every fixture with SportsKinetic.',
        image: DEFAULT_OG_IMAGE,
        imageWidth: DEFAULT_OG_IMAGE_WIDTH,
        imageHeight: DEFAULT_OG_IMAGE_HEIGHT,
        twitterImage: DEFAULT_TWITTER_IMAGE,
        twitterImageWidth: DEFAULT_TWITTER_IMAGE_WIDTH,
        twitterImageHeight: DEFAULT_TWITTER_IMAGE_HEIGHT,
    },
    {
        path: '/la-liga',
        priority: 0.9,
        changefreq: 'weekly',
        title: 'La Liga AI Predictions & Match Insights | SportsKinetic',
        description: 'Get AI-powered La Liga predictions, exact score forecasts and match insights for every fixture. Live now on SportsKinetic, alongside the Premier League.',
        image: DEFAULT_OG_IMAGE,
        imageWidth: DEFAULT_OG_IMAGE_WIDTH,
        imageHeight: DEFAULT_OG_IMAGE_HEIGHT,
        twitterImage: DEFAULT_TWITTER_IMAGE,
        twitterImageWidth: DEFAULT_TWITTER_IMAGE_WIDTH,
        twitterImageHeight: DEFAULT_TWITTER_IMAGE_HEIGHT,
    },
    {
        path: '/terms-and-conditions',
        priority: 0.3,
        changefreq: 'yearly',
        title: 'Terms and Conditions | SportsKinetic',
        description: "Read the terms and conditions governing your use of SportsKinetic's AI football prediction platform, subscriptions and related services.",
        image: DEFAULT_OG_IMAGE,
        imageWidth: DEFAULT_OG_IMAGE_WIDTH,
        imageHeight: DEFAULT_OG_IMAGE_HEIGHT,
        twitterImage: DEFAULT_TWITTER_IMAGE,
        twitterImageWidth: DEFAULT_TWITTER_IMAGE_WIDTH,
        twitterImageHeight: DEFAULT_TWITTER_IMAGE_HEIGHT,
    },
    {
        path: '/privacy-policy',
        priority: 0.3,
        changefreq: 'yearly',
        title: 'Privacy Policy | SportsKinetic',
        description: 'Learn how SportsKinetic collects, uses and protects your personal data across our AI football prediction platform. Read our full privacy policy.',
        image: DEFAULT_OG_IMAGE,
        imageWidth: DEFAULT_OG_IMAGE_WIDTH,
        imageHeight: DEFAULT_OG_IMAGE_HEIGHT,
        twitterImage: DEFAULT_TWITTER_IMAGE,
        twitterImageWidth: DEFAULT_TWITTER_IMAGE_WIDTH,
        twitterImageHeight: DEFAULT_TWITTER_IMAGE_HEIGHT,
    },
    {
        path: '/cancellation-policy',
        priority: 0.3,
        changefreq: 'yearly',
        title: 'Cancellation & Refund Policy | SportsKinetic',
        description: "Understand SportsKinetic's cancellation and refund policy for subscriptions, including how to cancel your plan and manage your billing.",
        image: DEFAULT_OG_IMAGE,
        imageWidth: DEFAULT_OG_IMAGE_WIDTH,
        imageHeight: DEFAULT_OG_IMAGE_HEIGHT,
        twitterImage: DEFAULT_TWITTER_IMAGE,
        twitterImageWidth: DEFAULT_TWITTER_IMAGE_WIDTH,
        twitterImageHeight: DEFAULT_TWITTER_IMAGE_HEIGHT,
    },
    {
        path: '/contact-us',
        priority: 0.5,
        changefreq: 'monthly',
        title: 'Contact Us | SportsKinetic',
        description: "Get in touch with the SportsKinetic team for support, partnership or media enquiries. We're here to help with any question about the platform.",
        image: DEFAULT_OG_IMAGE,
        imageWidth: DEFAULT_OG_IMAGE_WIDTH,
        imageHeight: DEFAULT_OG_IMAGE_HEIGHT,
        twitterImage: DEFAULT_TWITTER_IMAGE,
        twitterImageWidth: DEFAULT_TWITTER_IMAGE_WIDTH,
        twitterImageHeight: DEFAULT_TWITTER_IMAGE_HEIGHT,
    },
    {
        path: '/feedback',
        priority: 0.4,
        changefreq: 'monthly',
        title: 'Share Your Feedback | SportsKinetic',
        description: 'Tell us what you think of SportsKinetic. Share feedback, report an issue or suggest a feature to help us improve our AI football prediction platform.',
        image: DEFAULT_OG_IMAGE,
        imageWidth: DEFAULT_OG_IMAGE_WIDTH,
        imageHeight: DEFAULT_OG_IMAGE_HEIGHT,
        twitterImage: DEFAULT_TWITTER_IMAGE,
        twitterImageWidth: DEFAULT_TWITTER_IMAGE_WIDTH,
        twitterImageHeight: DEFAULT_TWITTER_IMAGE_HEIGHT,
    },
    {
        path: '/fixtures-preview',
        priority: 0.9,
        changefreq: 'daily',
        title: 'Fixture predictions, tips & news | SportsKinetic',
        description: 'See AI-powered predictions, tips and match previews for every upcoming fixture, with exact scorelines, form data and insights from SportsKinetic.',
        image: DEFAULT_OG_IMAGE,
        imageWidth: DEFAULT_OG_IMAGE_WIDTH,
        imageHeight: DEFAULT_OG_IMAGE_HEIGHT,
        twitterImage: DEFAULT_TWITTER_IMAGE,
        twitterImageWidth: DEFAULT_TWITTER_IMAGE_WIDTH,
        twitterImageHeight: DEFAULT_TWITTER_IMAGE_HEIGHT,
    },
    {
        path: '/guides/ai-football-predictions',
        priority: 0.7,
        changefreq: 'monthly',
        title: 'AI Football Predictions: The Complete Guide | SportsKinetic',
        description: 'How AI predicts football scores, goalscorers and match outcomes with confidence ratings, form data and live news. SportsKinetic predicts 1 in 5 Premier League scorelines exactly right.',
        image: DEFAULT_OG_IMAGE,
        imageWidth: DEFAULT_OG_IMAGE_WIDTH,
        imageHeight: DEFAULT_OG_IMAGE_HEIGHT,
        twitterImage: DEFAULT_TWITTER_IMAGE,
        twitterImageWidth: DEFAULT_TWITTER_IMAGE_WIDTH,
        twitterImageHeight: DEFAULT_TWITTER_IMAGE_HEIGHT,
    },
    {
        path: '/guides/what-is-btts',
        priority: 0.7,
        changefreq: 'monthly',
        title: 'What Is BTTS in Football? Both Teams to Score Explained | SportsKinetic',
        description: 'BTTS (Both Teams to Score) explained: what it means, what drives the prediction, and how SportsKinetic rates it with confidence for every fixture.',
        image: DEFAULT_OG_IMAGE,
        imageWidth: DEFAULT_OG_IMAGE_WIDTH,
        imageHeight: DEFAULT_OG_IMAGE_HEIGHT,
        twitterImage: DEFAULT_TWITTER_IMAGE,
        twitterImageWidth: DEFAULT_TWITTER_IMAGE_WIDTH,
        twitterImageHeight: DEFAULT_TWITTER_IMAGE_HEIGHT,
    },
    {
        path: '/guides/how-ai-sports-intelligence-works',
        priority: 0.7,
        changefreq: 'monthly',
        title: 'How AI Sports Intelligence Works | SportsKinetic',
        description: 'How AI turns millions of football data points into calibrated, clearly-explained predictions. The complete guide to the sports intelligence pipeline, end to end.',
        image: DEFAULT_OG_IMAGE,
        imageWidth: DEFAULT_OG_IMAGE_WIDTH,
        imageHeight: DEFAULT_OG_IMAGE_HEIGHT,
        twitterImage: DEFAULT_TWITTER_IMAGE,
        twitterImageWidth: DEFAULT_TWITTER_IMAGE_WIDTH,
        twitterImageHeight: DEFAULT_TWITTER_IMAGE_HEIGHT,
    },
    {
        path: '/guides/expected-goals-xg-ai',
        priority: 0.7,
        changefreq: 'monthly',
        title: 'What Is xG and How Is It Used in AI Models? | SportsKinetic',
        description: 'Expected goals (xG) explained: what it measures, how a chance gets its value, the key variants, and how AI prediction models use xG to forecast football.',
        image: DEFAULT_OG_IMAGE,
        imageWidth: DEFAULT_OG_IMAGE_WIDTH,
        imageHeight: DEFAULT_OG_IMAGE_HEIGHT,
        twitterImage: DEFAULT_TWITTER_IMAGE,
        twitterImageWidth: DEFAULT_TWITTER_IMAGE_WIDTH,
        twitterImageHeight: DEFAULT_TWITTER_IMAGE_HEIGHT,
    },
    {
        path: '/guides/can-ai-predict-football',
        priority: 0.7,
        changefreq: 'monthly',
        title: 'Can AI Really Predict Football Scores? | SportsKinetic',
        description: 'Can AI predict football scores? Not with certainty, but it turns match data into calibrated scoreline probabilities. Here is what AI can and cannot do.',
        image: DEFAULT_OG_IMAGE,
        imageWidth: DEFAULT_OG_IMAGE_WIDTH,
        imageHeight: DEFAULT_OG_IMAGE_HEIGHT,
        twitterImage: DEFAULT_TWITTER_IMAGE,
        twitterImageWidth: DEFAULT_TWITTER_IMAGE_WIDTH,
        twitterImageHeight: DEFAULT_TWITTER_IMAGE_HEIGHT,
    },
    {
        path: '/guides/ai-vs-human-predictions',
        priority: 0.7,
        changefreq: 'monthly',
        title: 'AI vs Human Pundits: Who Predicts Better? | SportsKinetic',
        description: 'AI vs human football pundits: who predicts better? Models win on calibration and consistency, humans on fresh context. The strongest predictions combine both.',
        image: DEFAULT_OG_IMAGE,
        imageWidth: DEFAULT_OG_IMAGE_WIDTH,
        imageHeight: DEFAULT_OG_IMAGE_HEIGHT,
        twitterImage: DEFAULT_TWITTER_IMAGE,
        twitterImageWidth: DEFAULT_TWITTER_IMAGE_WIDTH,
        twitterImageHeight: DEFAULT_TWITTER_IMAGE_HEIGHT,
    },
    {
        path: '/guides/football-prediction-accuracy',
        priority: 0.7,
        changefreq: 'monthly',
        title: 'How Accurate Are AI Football Predictions? | SportsKinetic',
        description: 'How accurate are AI football predictions? Accuracy depends on the market and sample, and calibration matters more than hit rate. Here is a realistic picture.',
        image: DEFAULT_OG_IMAGE,
        imageWidth: DEFAULT_OG_IMAGE_WIDTH,
        imageHeight: DEFAULT_OG_IMAGE_HEIGHT,
        twitterImage: DEFAULT_TWITTER_IMAGE,
        twitterImageWidth: DEFAULT_TWITTER_IMAGE_WIDTH,
        twitterImageHeight: DEFAULT_TWITTER_IMAGE_HEIGHT,
    },
    {
        path: '/track-record',
        priority: 0.8,
        changefreq: 'daily',
        title: 'Football Prediction Track Record: Winners and Losers | SportsKinetic',
        description: 'Every football prediction SportsKinetic has published, winners and losers, at the price we published it. Locked before kick-off, never edited, never removed.',
        image: DEFAULT_OG_IMAGE,
        imageWidth: DEFAULT_OG_IMAGE_WIDTH,
        imageHeight: DEFAULT_OG_IMAGE_HEIGHT,
        twitterImage: DEFAULT_TWITTER_IMAGE,
        twitterImageWidth: DEFAULT_TWITTER_IMAGE_WIDTH,
        twitterImageHeight: DEFAULT_TWITTER_IMAGE_HEIGHT,
    },
    {

        path: '/ai-football-analysis',
        priority: 0.6,
        changefreq: 'monthly',
        title: 'AI Football Predictions, News & Insights | SportsKinetic',
        description: 'SportsKinetic delivers AI-powered football analysis, match previews, player insights, transfer news and fixture analysis, all in one smart platform.',
        image: DEFAULT_OG_IMAGE,
        imageWidth: DEFAULT_OG_IMAGE_WIDTH,
        imageHeight: DEFAULT_OG_IMAGE_HEIGHT,
        twitterImage: DEFAULT_TWITTER_IMAGE,
        twitterImageWidth: DEFAULT_TWITTER_IMAGE_WIDTH,
        twitterImageHeight: DEFAULT_TWITTER_IMAGE_HEIGHT,
    },
    {
        
        path: '/affiliate-campaign',
        priority: 0.6,
        changefreq: 'monthly',
        title: 'SportsKinetic Partner Programme | Affiliate With Us',
        description: `Earn recurring commission promoting SportsKinetic, football news and analysis on a ${formatPrice(MONTHLY_PRICE)}/mo subscription. Brand-safe, not betting.`,
        image: DEFAULT_OG_IMAGE,
        imageWidth: DEFAULT_OG_IMAGE_WIDTH,
        imageHeight: DEFAULT_OG_IMAGE_HEIGHT,
        twitterImage: DEFAULT_TWITTER_IMAGE,
        twitterImageWidth: DEFAULT_TWITTER_IMAGE_WIDTH,
        twitterImageHeight: DEFAULT_TWITTER_IMAGE_HEIGHT,
    },
];

// Single source of truth for a prerendered/SSR'd route's <SeoHelmet> props. Page components
// for every path in seoRoutes MUST pull title/description/image/etc. from here (spread the
// result into <SeoHelmet>) rather than retyping the same values, otherwise the client-rendered
// <head> drifts from what src/entry-server.js / lambda/ssr-handler.js render — react-helmet-async
// overwrites the SSR'd tags on hydration, so any mismatch becomes what crawlers/social scrapers
// see after JS runs.
export const getSeoRoute = (path) => seoRoutes.find((route) => route.path === path);
